import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ReactPlayer from "react-player/lazy";
import {
  FaVolumeMute,
  FaVolumeUp,
  FaRegThumbsUp,
  FaThumbsUp,
  FaRegThumbsDown,
  FaThumbsDown,
  FaComment,
  FaShareAlt,
  FaChevronUp,
  FaChevronDown,
} from "react-icons/fa";
import { Translate, I18n } from "react-redux-i18n";
import UserAvatar from "./UserAvatar";

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function ReelViewerSlide({
  item,
  isActive,
  muted,
  onToggleMute,
  likeState,
  onLike,
  onDislike,
  onOpenComments,
  onOpenShare,
  onGoNext,
  onGoPrev,
  hasNext,
  hasPrev,
  adMeta,
}) {
  const videoRef = useRef(null);
  const isYoutube = item.Type === "youtube";
  const likeStatus = likeState?.status ?? adMeta?.Like?.Status ?? null;
  const likeUp = likeState?.up ?? adMeta?.Like?.Up ?? 0;
  const likeDown = likeState?.down ?? adMeta?.Like?.Down ?? 0;
  const commentCount = adMeta?.CommentCount ?? 0;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || isYoutube) return;

    if (isActive) {
      const playPromise = el.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    } else {
      el.pause();
      el.currentTime = 0;
    }
  }, [isActive, isYoutube, item.VideoUrl]);

  useEffect(() => {
    const el = videoRef.current;
    if (el) {
      el.muted = muted;
    }
  }, [muted]);

  return (
    <section className="reels-viewer-slide" data-active={isActive ? "true" : "false"}>
      <div className="reels-viewer-slide__media">
        {isYoutube ? (
          <ReactPlayer
            className="reels-viewer-slide__player"
            url={item.VideoUrl}
            playing={isActive}
            muted={muted}
            loop
            playsinline
            width="100%"
            height="100%"
            config={{
              youtube: {
                playerVars: {
                  playsinline: 1,
                  modestbranding: 1,
                  rel: 0,
                },
              },
            }}
          />
        ) : (
          <video
            ref={videoRef}
            className="reels-viewer-slide__video"
            src={item.VideoUrl}
            playsInline
            loop
            muted={muted}
            preload="metadata"
          />
        )}
        <div className="reels-viewer-slide__shade" aria-hidden="true" />
      </div>

      <div className="reels-viewer-slide__side">
        <button
          type="button"
          className={`reels-viewer-action${likeStatus === 1 ? " is-active-up" : ""}`}
          onClick={onLike}
          aria-label={I18n.t("ad_like")}
        >
          {likeStatus === 1 ? <FaThumbsUp /> : <FaRegThumbsUp />}
          <span>{likeUp}</span>
        </button>
        <button
          type="button"
          className={`reels-viewer-action${likeStatus === 0 ? " is-active-down" : ""}`}
          onClick={onDislike}
          aria-label={I18n.t("ad_dislike")}
        >
          {likeStatus === 0 ? <FaThumbsDown /> : <FaRegThumbsDown />}
          <span>{likeDown}</span>
        </button>
        <button
          type="button"
          className="reels-viewer-action"
          onClick={onOpenComments}
          aria-label={I18n.t("comments")}
        >
          <FaComment />
          <span>{commentCount}</span>
        </button>
        <button
          type="button"
          className="reels-viewer-action"
          onClick={onToggleMute}
          aria-label={muted ? I18n.t("reel_sound_on") : I18n.t("reel_sound_off")}
        >
          {muted ? <FaVolumeMute /> : <FaVolumeUp />}
        </button>
        <button
          type="button"
          className="reels-viewer-action"
          onClick={onOpenShare}
          aria-label={I18n.t("ad_share_title")}
        >
          <FaShareAlt />
        </button>
      </div>

      <div className="reels-viewer-slide__info">
        <div className="reels-viewer-slide__user">
          {item.User?.Avatar ? (
            <UserAvatar
              src={item.User.Avatar}
              alt={item.User.Name || ""}
              user={item.User}
              size="sm"
              imgClassName="reels-viewer-slide__avatar"
              initials={getInitials(item.User?.Name)}
            />
          ) : (
            <div className="reels-viewer-slide__avatar reels-viewer-slide__avatar--placeholder">
              {getInitials(item.User?.Name)}
            </div>
          )}
          <div className="reels-viewer-slide__identity">
            <strong>{item.User?.Name}</strong>
            {item.User?.Username && <span>@{item.User.Username}</span>}
          </div>
        </div>
        <h2 className="reels-viewer-slide__title">{item.Title}</h2>
        {item.ProductId && (
          <Link to={`/goder/show/${item.ProductId}`} className="reels-viewer-slide__ad-link">
            <Translate value="reel_view_ad" />
          </Link>
        )}
      </div>

      <div className="reels-viewer-slide__nav" aria-hidden="true">
        {hasPrev && (
          <button type="button" className="reels-viewer-slide__nav-btn" onClick={onGoPrev}>
            <FaChevronUp />
          </button>
        )}
        {hasNext && (
          <button type="button" className="reels-viewer-slide__nav-btn" onClick={onGoNext}>
            <FaChevronDown />
          </button>
        )}
      </div>
    </section>
  );
}

export default ReelViewerSlide;
